import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Loader from "../Loader";
import Pagination from "../Pagination";
import AuthContext from "../../contexts/AuthProvider";
import { API_URL } from "../../utils/config";
const baseUrl = API_URL;

const BookingsTable = ({ onCountChange, statusFilter, skipInitialCountUpdate = false }) => {
  const { t } = useTranslation("booking");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0
  });
  const { isAuthenticated } = useContext(AuthContext);

  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };

  const fetchBooking = async (page = 1) => {
    try {
      if (!isAuthenticated) {
        localStorage.removeItem("token");
        navigate("/login");
        return { data: [], pagination: { currentPage: 1, totalPages: 1, totalItems: 0 } };
      }

      const response = await fetch(`${baseUrl}/api/v1/attnd-venue/bookings?page=${page}`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),
        },
      });

      if (response.status === 401) {
        navigate("/login");
        throw new Error("Unauthorized access. Please log in again.");
      }
      if (!response.ok) {
        throw new Error("Failed to fetch bookings.");
      }

      const json = await response.json();
      return {
        data: json.data,
        pagination: {
          currentPage: json.currentPage,
          totalPages: json.totalPages,
          totalItems: json.totalItems
        }
      };
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
      }
      return { data: [], pagination: { currentPage: 1, totalPages: 1, totalItems: 0 } };
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const result = await fetchBooking(1);
      setBookings(result.data);
      setPagination(result.pagination);
      
      // Notify parent component about the count only if not skipping initial update
      if (onCountChange && !skipInitialCountUpdate) {
        onCountChange(result.pagination.totalItems);
      }
      
      setLoading(false);
    })();
  }, [onCountChange]);

  const handlePageChange = async (newPage) => {
    if (newPage === pagination.currentPage) return;
    
    setLoading(true);
    const result = await fetchBooking(newPage);
    setBookings(result.data);
    setPagination(result.pagination);
    setLoading(false);
  };

  const transformedBookings = bookings.map((booking) => ({
    id: booking.id,
    venue_name: booking.venue_name || "Unknown",
    start: booking.start
      ? new Date(booking.start)
          .toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
          .replace(",", " -")
      : "Unknown",
    end: booking.end
      ? new Date(booking.end)
          .toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
          .replace(",", " -")
      : "Unknown",
    user_name: booking.user_name || "Unknown",
    status: booking.status || "Unknown",
  }));

  const filteredBookings = transformedBookings.filter((booking) =>
    statusFilter === "allStatus" || booking.status === statusFilter
  );

  const handleBookingDetails = (id) => {
    navigate(`/booking-details/${id}`);
  };

  if (loading) {
    return (
      <div className="booking-loader-container">
        <Loader />
      </div>
    );
  }

  return (
    <div className="bookings-table-container">

      {/* Bookings Table */}
      <div className="booking-table table-wrapper">
        <table className="bookings-table">
          <thead>
            <tr>
              <th>{t("tableHeaders.customer")}</th>
              <th>{t("tableHeaders.venue")}</th>
              <th>{t("tableHeaders.startDate")}</th>
              <th>{t("tableHeaders.endDate")}</th>
              <th>{t("tableHeaders.status")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredBookings.length > 0 ? (
              filteredBookings.map((booking) => {
                const key = booking.status.toLowerCase();
                return (
                  <tr
                    key={booking.id}
                    onClick={() => handleBookingDetails(booking.id)}
                  >
                    <td>{booking.user_name}</td>
                    <td>{booking.venue_name}</td>
                    <td>{booking.start}</td>
                    <td>{booking.end}</td>
                    <td>
                      <span
                        className={`status-badge ${
                          (key === "accepted" || key === "paid") &&
                          "confirmed-booking"
                        } ${
                          (key === "rejected" || key === "cancelled" || key === "no_responded") &&
                          "rejected"
                        } ${
                          key === "processing" && "pending-processing"
                        } ${
                          key === "pending" && "pending-status"
                        }`}
                      >
                        {t(`badgeStatus.${key}`)}
                      </span>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="6" className="no-data">
                  {t("noData")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="booking-mobile-table">
        {filteredBookings.length > 0 ? (
          <ul>
            {filteredBookings.map((booking) => {
              const key = booking.status.toLowerCase();
              return (
                <li
                  key={booking.id}
                  className="booking-card"
                  onClick={() => handleBookingDetails(booking.id)}
                >
                  <div className="booking-card-content">
                    <div className="booking-name">
                      {booking.user_name}, {booking.venue_name}
                    </div>
                    <div className="booking-info">
                      {booking.start}, {booking.end}
                    </div>
                  </div>
                  <div className="booking-status">
                    <span
                        className={`status-badge ${
                          (key === "accepted" || key === "paid") &&
                          "confirmed-booking"
                        } ${
                          (key === "rejected" || key === "cancelled") &&
                          "rejected"
                        } ${
                          key === "processing" && "pending-processing"
                        } ${
                          key === "pending" && "pending-status"
                        }`}
                      >
                        {t(`badgeStatus.${key}`)}
                      </span>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <div>
            <div className="no-data">{t("noData")}</div>
          </div>
        )}
      </div>

      {/* Pagination Component */}
      {pagination.totalPages > 1 && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          handlePageChange={handlePageChange}
          maxVisiblePages={5}
          isRTL={getLangFromLocalStorage() === "ar"}
        />
      )}
    </div>
  );
};

export default BookingsTable;